/*
 * Analytics tracking vocabulary and step maps for the widget.
 *
 * `stepId` values are STABLE machine slugs — they must not change across releases
 * so a venue's GA4 funnels keep working. `stepName` is a fixed human label for
 * reports (intentionally locale-independent so funnels don't fragment per visitor
 * language). Internal, non-user-facing steps (e.g. loading screens) are simply
 * absent from these maps, so they never emit a `constell_step_viewed`.
 */
import {
    type WidgetModuleSlugTyping,
    type WidgetStepDescriptorTyping,
    type WidgetTrackingEventNameTyping,
} from '@shared/typings';

// Canonical event-name vocabulary (same string for CustomEvent type and dataLayer event).
export const TRACKING_EVENTS: Record<string, WidgetTrackingEventNameTyping> = {
    WIDGET_OPENED: 'constell_widget_opened',
    MODULE_STARTED: 'constell_module_started',
    STEP_VIEWED: 'constell_step_viewed',
    REQUEST_SUBMITTED: 'constell_request_submitted',
    WIDGET_CLOSED: 'constell_widget_closed',
    PARTNER_CLICKED: 'constell_partner_clicked',
};

// Canonical module slugs (5 implemented modules; `private_dining` not yet built).
export const WIDGET_MODULES: Record<string, WidgetModuleSlugTyping> = {
    QUOTE: 'quote',
    SELF_QUOTE: 'self_quote',
    PACKAGES: 'packages',
    APPOINTMENT: 'appointment',
    CONTACT_FORM: 'contact_form',
};

// `totalSteps` is null for every module: each flow has conditional/optional screens,
// so the length is dynamic. `stepIndex` carries the ordering instead.
const DYNAMIC_TOTAL = null;

/*
 * Self-quote builder (apps/widget/src/components/quote/Quote.ce.vue).
 * Keyed by the component's numeric `step` ref. Step 12 (loading) is intentionally omitted.
 */
export const QUOTE_STEP_MAP: Record<number, WidgetStepDescriptorTyping> = {
    0: {
        stepId: 'tutorial',
        stepName: 'Tutorial',
        stepIndex: 1,
        totalSteps: DYNAMIC_TOTAL,
    },
    1: {
        stepId: 'event_type',
        stepName: 'Event type',
        stepIndex: 2,
        totalSteps: DYNAMIC_TOTAL,
    },
    2: {
        stepId: 'guest_type',
        stepName: 'Guest type',
        stepIndex: 3,
        totalSteps: DYNAMIC_TOTAL,
    },
    3: {
        stepId: 'timeframe',
        stepName: 'Timeframe',
        stepIndex: 4,
        totalSteps: DYNAMIC_TOTAL,
    },
    4: {
        stepId: 'date',
        stepName: 'Date',
        stepIndex: 5,
        totalSteps: DYNAMIC_TOTAL,
    },
    5: {
        stepId: 'appointment_survey',
        stepName: 'Appointment survey',
        stepIndex: 6,
        totalSteps: DYNAMIC_TOTAL,
    },
    6: {
        stepId: 'template',
        stepName: 'Template',
        stepIndex: 7,
        totalSteps: DYNAMIC_TOTAL,
    },
    8: {
        stepId: 'program',
        stepName: 'Program',
        stepIndex: 8,
        totalSteps: DYNAMIC_TOTAL,
    },
    9: {
        stepId: 'decoration',
        stepName: 'Decoration',
        stepIndex: 9,
        totalSteps: DYNAMIC_TOTAL,
    },
    10: {
        stepId: 'entertainment',
        stepName: 'Entertainment',
        stepIndex: 10,
        totalSteps: DYNAMIC_TOTAL,
    },
    11: {
        stepId: 'space',
        stepName: 'Space',
        stepIndex: 11,
        totalSteps: DYNAMIC_TOTAL,
    },
    13: {
        stepId: 'product_category',
        stepName: 'Product category',
        stepIndex: 12,
        totalSteps: DYNAMIC_TOTAL,
    },
    14: {
        stepId: 'products',
        stepName: 'Products',
        stepIndex: 13,
        totalSteps: DYNAMIC_TOTAL,
    },
    15: {
        stepId: 'contact_details',
        stepName: 'Contact details',
        stepIndex: 14,
        totalSteps: DYNAMIC_TOTAL,
    },
    16: {
        stepId: 'confirmation',
        stepName: 'Confirmation',
        stepIndex: 15,
        totalSteps: DYNAMIC_TOTAL,
    },
};

/*
 * Guided journey / "Online host" (apps/widget/src/views/PathView.ce.vue).
 * Keyed by the path store's integer `step`. Fractional (.5) animation states and
 * the loading step (15) are intentionally omitted.
 */
export const PATH_STEP_MAP: Record<number, WidgetStepDescriptorTyping> = {
    1: {
        stepId: 'event_type',
        stepName: 'Event type',
        stepIndex: 1,
        totalSteps: DYNAMIC_TOTAL,
    },
    2: {
        stepId: 'booker_name',
        stepName: 'Your name',
        stepIndex: 2,
        totalSteps: DYNAMIC_TOTAL,
    },
    3: {
        stepId: 'greetings',
        stepName: 'Greetings',
        stepIndex: 3,
        totalSteps: DYNAMIC_TOTAL,
    },
    4: {
        stepId: 'path_selection',
        stepName: 'Path selection',
        stepIndex: 4,
        totalSteps: DYNAMIC_TOTAL,
    },
    5: {
        stepId: 'date',
        stepName: 'Date',
        stepIndex: 5,
        totalSteps: DYNAMIC_TOTAL,
    },
    6: {
        stepId: 'appointment_survey',
        stepName: 'Appointment survey',
        stepIndex: 6,
        totalSteps: DYNAMIC_TOTAL,
    },
    7: {
        stepId: 'survey_organizer',
        stepName: 'Organizer survey',
        stepIndex: 7,
        totalSteps: DYNAMIC_TOTAL,
    },
    8: {
        stepId: 'survey_theme',
        stepName: 'Theme survey',
        stepIndex: 8,
        totalSteps: DYNAMIC_TOTAL,
    },
    9: {
        stepId: 'guest_count',
        stepName: 'Guest count',
        stepIndex: 9,
        totalSteps: DYNAMIC_TOTAL,
    },
    10: {
        stepId: 'descriptions',
        stepName: 'Descriptions',
        stepIndex: 10,
        totalSteps: DYNAMIC_TOTAL,
    },
    11: {
        stepId: 'budget',
        stepName: 'Budget',
        stepIndex: 11,
        totalSteps: DYNAMIC_TOTAL,
    },
    12: {
        stepId: 'appointment_choice',
        stepName: 'Appointment choice',
        stepIndex: 12,
        totalSteps: DYNAMIC_TOTAL,
    },
    13: {
        stepId: 'appointment_calendar',
        stepName: 'Appointment calendar',
        stepIndex: 13,
        totalSteps: DYNAMIC_TOTAL,
    },
    16: {
        stepId: 'summary',
        stepName: 'Summary',
        stepIndex: 14,
        totalSteps: DYNAMIC_TOTAL,
    },
    17: {
        stepId: 'pre_text',
        stepName: 'Almost there',
        stepIndex: 15,
        totalSteps: DYNAMIC_TOTAL,
    },
    18: {
        stepId: 'confirmation',
        stepName: 'Confirmation',
        stepIndex: 16,
        totalSteps: DYNAMIC_TOTAL,
    },
};

/*
 * Packages (apps/widget/src/components/packages/PackageSelection.ce.vue).
 * Keyed by the active boolean-view name. Order is stable per screen even though
 * the visited path differs (business path vs. browse path).
 */
export const PACKAGE_STEP_MAP: Record<string, WidgetStepDescriptorTyping> = {
    packageListView: {
        stepId: 'package_list',
        stepName: 'Package list',
        stepIndex: 1,
        totalSteps: DYNAMIC_TOTAL,
    },
    packageView: {
        stepId: 'package_detail',
        stepName: 'Package detail',
        stepIndex: 2,
        totalSteps: DYNAMIC_TOTAL,
    },
    dateView: {
        stepId: 'date',
        stepName: 'Date & time',
        stepIndex: 3,
        totalSteps: DYNAMIC_TOTAL,
    },
    spaceView: {
        stepId: 'space',
        stepName: 'Space',
        stepIndex: 4,
        totalSteps: DYNAMIC_TOTAL,
    },
    productView: {
        stepId: 'products',
        stepName: 'Products',
        stepIndex: 5,
        totalSteps: DYNAMIC_TOTAL,
    },
    formView: {
        stepId: 'contact_details',
        stepName: 'Contact details',
        stepIndex: 6,
        totalSteps: DYNAMIC_TOTAL,
    },
    confirmationView: {
        stepId: 'confirmation',
        stepName: 'Confirmation',
        stepIndex: 7,
        totalSteps: DYNAMIC_TOTAL,
    },
};

// Appointment (apps/widget/src/components/Appointment.ce.vue + AppointmentForm.ce.vue).
export const APPOINTMENT_STEPS: Record<string, WidgetStepDescriptorTyping> = {
    DATE_TIME: {
        stepId: 'date_time',
        stepName: 'Date & time',
        stepIndex: 1,
        totalSteps: DYNAMIC_TOTAL,
    },
    CONTACT_DETAILS: {
        stepId: 'contact_details',
        stepName: 'Contact details',
        stepIndex: 2,
        totalSteps: DYNAMIC_TOTAL,
    },
    CONFIRMATION: {
        stepId: 'confirmation',
        stepName: 'Confirmation',
        stepIndex: 3,
        totalSteps: DYNAMIC_TOTAL,
    },
};

// Contact form (apps/widget/src/components/FullForm.ce.vue).
export const CONTACT_FORM_STEPS: Record<string, WidgetStepDescriptorTyping> = {
    CONTACT_DETAILS: {
        stepId: 'contact_details',
        stepName: 'Contact details',
        stepIndex: 1,
        totalSteps: DYNAMIC_TOTAL,
    },
    CONFIRMATION: {
        stepId: 'confirmation',
        stepName: 'Confirmation',
        stepIndex: 2,
        totalSteps: DYNAMIC_TOTAL,
    },
};
